/**
 * Lightweight analytics helper.
 * Events are pushed to the GTM dataLayer (GTM is loaded in index.html) and,
 * when a gtag instance exists (GA4 via GTM or direct), sent to GA4 as well.
 */

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

export type AnalyticsParams = Record<string, string | number | boolean | undefined>;

/** mobile | desktop, matching the funnel breakdown used in GA4. */
export function getDevice() {
  if (typeof window === "undefined") return "unknown";
  return window.innerWidth < 1024 ? "mobile" : "desktop";
}

const FUNNEL_PAGE_KEY = "tron_funnel_page";

/** The page that started the current funnel session (survives the Web3Forms redirect). */
export function setFunnelPage(path: string) {
  try {
    window.sessionStorage.setItem(FUNNEL_PAGE_KEY, path);
  } catch {
    /* ignore */
  }
}

export function getFunnelPage() {
  try {
    return window.sessionStorage.getItem(FUNNEL_PAGE_KEY) || window.location.pathname;
  } catch {
    return window.location.pathname;
  }
}

export function trackEvent(event: string, params: AnalyticsParams = {}) {
  if (typeof window === "undefined") return;

  const payload: AnalyticsParams = {
    page_path: window.location.pathname,
    page_location: window.location.href,
    page_title: document.title,
    // Shared funnel dimensions: every step reports the same page + device,
    // so page_view -> form_submit -> generate_lead can be compared 1:1.
    funnel_page: window.location.pathname,
    device: getDevice(),
    ...params,
  };

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...payload });
    if (typeof window.gtag === "function") {
      window.gtag("event", event, payload);
    }
  } catch {
    // analytics must never break the UI
  }
}

/** Normalizes a tel:/sms: href into digits for consistent reporting. */
export function normalizeNumber(href: string) {
  return href.replace(/^(tel:|sms:)/i, "").replace(/[^\d+]/g, "");
}
